import { useState, useEffect, useMemo } from 'react';
import { Promotion, PromotionFormData, PromotionFilters, PromotionStats } from '../types';
import { promotionsService } from '../services/promotions.service';

export const usePromotions = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [stats, setStats] = useState<PromotionStats>({
    metrics: {
      totalPromotions: 0,
      activePromotions: 0,
      totalSavings: 0,
      averageUsage: 0
    },
    topPromotions: [],
    typeDistribution: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PromotionFilters>({
    search: '',
    type: 'all',
    isActive: 'all'
  });

  // Filtrar promociones basado en los filtros
  const filteredPromotions = useMemo(() => {
    return promotions.filter(promotion => {
      const matchesSearch = promotion.name.toLowerCase().includes(filters.search.toLowerCase()) ||
                           promotion.description.toLowerCase().includes(filters.search.toLowerCase());
      
      const matchesType = filters.type === 'all' || promotion.type === filters.type;
      const matchesActive = filters.isActive === 'all' ||
        (filters.isActive === true && promotion.isActive) ||
        (filters.isActive === false && !promotion.isActive);

      return matchesSearch && matchesType && matchesActive;
    });
  }, [promotions, filters]);

  // Cargar promociones
  const loadPromotions = async () => {
    try {
      setLoading(true);
      setError(null);
      const [promotionsData, statsData] = await Promise.all([
        promotionsService.getPromotions(),
        promotionsService.getPromotionStats()
      ]);
      setPromotions(promotionsData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar promociones');
    } finally {
      setLoading(false);
    }
  };

  // Crear promoción
  const createPromotion = async (data: PromotionFormData) => {
    try {
      const newPromotion = await promotionsService.createPromotion(data);
      setPromotions(prev => [...prev, newPromotion]);
      await loadPromotions(); // Recargar stats
      return newPromotion;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al crear promoción');
      throw err;
    }
  };

  // Actualizar promoción
  const updatePromotion = async (id: number, data: PromotionFormData) => {
    try {
      const updatedPromotion = await promotionsService.updatePromotion(id, data);
      setPromotions(prev => prev.map(p => p.id === id ? updatedPromotion : p));
      await loadPromotions(); // Recargar stats
      return updatedPromotion;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar promoción');
      throw err;
    }
  };

  // Eliminar promoción
  const deletePromotion = async (id: number) => {
    try {
      await promotionsService.deletePromotion(id);
      setPromotions(prev => prev.filter(p => p.id !== id));
      await loadPromotions(); // Recargar stats
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar promoción');
      throw err;
    }
  };

  // Alternar estado de promoción
  const togglePromotionStatus = async (id: number, isActive: boolean) => {
    try {
      const updatedPromotion = await promotionsService.togglePromotionStatus(id, isActive);
      setPromotions(prev => prev.map(p => p.id === id ? updatedPromotion : p));
      await loadPromotions(); // Recargar stats
      return updatedPromotion;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cambiar estado de promoción');
      throw err;
    }
  };

  // Cargar datos al montar
  useEffect(() => {
    loadPromotions();
  }, []);

  return {
    promotions,
    filteredPromotions,
    stats,
    loading,
    error,
    filters,
    setFilters,
    createPromotion,
    updatePromotion,
    deletePromotion,
    togglePromotionStatus,
    loadPromotions
  };
};